import React, { useState } from 'react';
import {
  Check,
  Globe,
  Link as LinkIcon,
  SignIn,
  AppleLogo,
  GooglePlayLogo,
  QrCode,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { APP_STORE_LINKS } from '@/lib/appStoreLinks';

/** Re-exported so existing importers keep working. */
export { APP_STORE_LINKS };

interface CustomerLinkShareProps {
  customerPortalUrl: string;
  disabled?: boolean;
  /** Controlled open — used by the mobile header menu as an anchor. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the icon trigger; pair with controlled open from a parent menu item. */
  hideTrigger?: boolean;
}

type LinkType = 'register' | 'login' | 'ios' | 'android';

import { AppStoreQrDialog, type AppStore } from './AppStoreQrDialog';

/**
 * Copy-to-share dropdown for customer portal and app-store links.
 * Lives in the page-header utility cluster next to weather / notifications.
 */
export function CustomerLinkShare({
  customerPortalUrl,
  disabled,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: CustomerLinkShareProps) {
  const [copiedLink, setCopiedLink] = useState<LinkType | null>(null);
  // Which store's install code is open, if any (the QR button on the iOS / Android rows).
  const [qrStore, setQrStore] = useState<AppStore | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const handleCopy = async (type: LinkType) => {
    const urls: Record<LinkType, string> = {
      register: customerPortalUrl,
      login: customerPortalUrl ? `${customerPortalUrl}/login` : '',
      ios: APP_STORE_LINKS.ios,
      android: APP_STORE_LINKS.android,
    };

    const url = urls[type];
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(type);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const links: {
    type: LinkType;
    label: string;
    subtitle: string;
    icon: React.ReactNode;
    color: string;
    disabled?: boolean;
  }[] = [
    {
      type: 'register',
      label: 'Register',
      subtitle: 'New customer sign-up',
      icon: <Globe className="h-4 w-4" weight="light" />,
      color: 'bg-primary/10 text-primary',
      disabled: !customerPortalUrl,
    },
    {
      type: 'login',
      label: 'Login',
      subtitle: 'Existing customer sign-in',
      icon: <SignIn className="h-4 w-4" weight="light" />,
      color: 'bg-orange-50 text-orange-600',
      disabled: !customerPortalUrl,
    },
    {
      type: 'ios',
      label: 'iOS App',
      subtitle: 'App Store',
      icon: <AppleLogo className="h-4 w-4" weight="light" />,
      color: 'bg-stone-50 text-stone-600',
    },
    {
      type: 'android',
      label: 'Android App',
      subtitle: 'Play Store',
      icon: <GooglePlayLogo className="h-4 w-4" weight="light" />,
      color: 'bg-green-50 text-green-600',
    },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {hideTrigger ? (
        <DropdownMenuTrigger asChild>
          <span
            aria-hidden
            tabIndex={-1}
            className="pointer-events-none fixed right-12 top-2.5 h-9 w-9 opacity-0"
          />
        </DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label={copiedLink ? 'Copied!' : 'Share links'}
                className="flex items-center justify-center h-9 w-9 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <div className="relative h-4 w-4 flex-shrink-0">
                  <LinkIcon
                    className={cn(
                      'absolute inset-0 h-4 w-4 transition-all duration-200',
                      copiedLink ? 'opacity-0 scale-75' : 'opacity-100 scale-100',
                    )}
                    weight="light"
                  />
                  <Check
                    className={cn(
                      'absolute inset-0 h-4 w-4 text-green-400 transition-all duration-200',
                      copiedLink ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
                    )}
                    weight="bold"
                  />
                </div>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {copiedLink ? 'Copied!' : 'Share links'}
          </TooltipContent>
        </Tooltip>
      )}

      <DropdownMenuContent
        side="bottom"
        align="end"
        className={cn('w-56 p-1.5', hideTrigger && 'z-[110]')}
      >
        <div className="px-2 py-1.5 mb-1">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            Copy link to share
          </p>
        </div>

        {links.map(({ type, label, subtitle, icon, color, disabled: itemDisabled }) => (
          <DropdownMenuItem
            key={type}
            onClick={() => handleCopy(type)}
            disabled={itemDisabled}
            className="group cursor-pointer rounded-md px-2 py-2"
          >
            <div
              className={cn(
                'flex items-center justify-center h-7 w-7 rounded-md mr-2.5 transition-colors',
                copiedLink === type ? 'bg-green-50 text-green-600' : color,
              )}
            >
              {copiedLink === type ? (
                <Check className="h-3.5 w-3.5" weight="bold" />
              ) : (
                icon
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium leading-tight">
                {copiedLink === type ? 'Copied!' : label}
              </div>
              <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                {subtitle}
              </div>
            </div>
            {(type === 'ios' || type === 'android') && (
              <button
                type="button"
                aria-label={`Show QR code for ${label}`}
                onClick={(e) => {
                  // The row itself copies the link; this opens the code instead.
                  e.stopPropagation();
                  e.preventDefault();
                  setOpen(false);
                  setQrStore(type);
                }}
                className="ml-2 flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] font-medium text-stone-500 opacity-0 transition-opacity hover:bg-stone-100 hover:text-stone-900 focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 group-data-[highlighted]:opacity-100"
              >
                <QrCode className="h-3.5 w-3.5" />
                QR
              </button>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
      {qrStore && (
        <AppStoreQrDialog open onOpenChange={(isOpen) => { if (!isOpen) setQrStore(null); }} store={qrStore} />
      )}
    </DropdownMenu>
  );
}
